import { Fragment } from 'react';
import {
  RadioGroup as AriaRadioGroup,
  type RadioGroupProps as AriaRadioGroupProps,
} from 'react-aria-components';

import type { FieldProps } from '../../../types';
import { mergeClassNames } from '../../../utils/mergeClassNames';
import { VisuallyHidden } from '../../display/VisuallyHidden';
import { Description } from '../Description';
import { ErrorMessage } from '../ErrorMessage';
import { Label } from '../Label';

import styles from './RadioGroup.module.scss';

export type RadioGroupProps = AriaRadioGroupProps & FieldProps;

/**
 * `RadioGroup` allows a user to select an item from a list of mutually
 * exclusive options. Built using React aria's
 * [RadioGroup](https://react-spectrum.adobe.com/react-aria/RadioGroup.html)
 * component.
 *
 * Its children must be `Radio` components, each supplied with a unique `value`
 * prop.
 *
 * ```tsx
 * <RadioGroup label="Radio Group Label" defaultValue="option1">
 *   <Radio value="option1">Option 1</Radio>
 *   <Radio value="option2">Option 2</Radio>
 *   <Radio value="option3" isDisabled>Option 3</Radio>
 *   <Radio value="option4">Option 4</Radio>
 * </RadioGroup>
 * ```
 */
export const RadioGroup = ({
  children,
  className,
  isRequired,
  label,
  helpText,
  errorMessage,
  isLabelHidden,
  ...props
}: RadioGroupProps) => {
  const LabelWrapper = isLabelHidden ? VisuallyHidden : Fragment;

  return (
    <AriaRadioGroup
      className={(renderProps) =>
        mergeClassNames([styles.radioGroup, className], renderProps)
      }
      data-zds
      isRequired={isRequired}
      {...props}
    >
      {(renderProps) => (
        <>
          {label && (
            <LabelWrapper>
              <Label isRequired={isRequired}>{label}</Label>
            </LabelWrapper>
          )}
          {typeof children === 'function' ? children(renderProps) : children}
          {helpText && <Description>{helpText}</Description>}
          <ErrorMessage>{errorMessage}</ErrorMessage>
        </>
      )}
    </AriaRadioGroup>
  );
};
